// What a post to the USGA would send, in plain words — shared by the posting row's preview and the
// total-only post panel, so both describe a post the same way.
import type { UsgaPostSent } from '../types/UsgaPost';

type UsgaPostHole = NonNullable<UsgaPostSent['holes']>[number];

/**
 * One hole as it would be sent. A hole in `xHolesFromUsgaIndex` was entered as a bare X for a golfer
 * whose Handicap Index® Gratigolf doesn't have: the server looks it up with the USGA when posting,
 * so the value shown in the build (par + 5) isn't what will be sent.
 */
function holeText(hole: UsgaPostHole, xHolesFromUsgaIndex: ReadonlySet<number>): string {
  if (hole.xHole) return `${hole.holeNumber}: not played`;
  if (hole.mostLikelyScore != null) return `${hole.holeNumber}: x${hole.mostLikelyScore}`;
  if (xHolesFromUsgaIndex.has(hole.holeNumber)) return `${hole.holeNumber}: X (net double bogey, from the USGA's index when posting)`;
  return `${hole.holeNumber}: ${hole.rawScore}`;
}

export function usgaPostPreviewLines(sent: UsgaPostSent, xHolesFromUsgaIndex: ReadonlySet<number> = new Set()): string[] {
  const kind = sent.method === 'HOLE_BY_HOLE' ? 'Hole by hole' : `Total, adjusted score ${sent.adjustedGrossScore}`;
  const lines = [
    `${kind} · ${sent.numberOfPlayedHoles} of ${sent.numberOfHoles} holes (${sent.teeSetSide}) · ${sent.playedAt}`,
    `USGA course ${sent.courseId} · tee set ${sent.teeSetId} · ${sent.gender === 'F' ? 'Women' : 'Men'}`,
  ];
  if (sent.holes) lines.push(sent.holes.map((h) => holeText(h, xHolesFromUsgaIndex)).join(' · '));
  return lines;
}
